import { attachClient, createApiClient, parkedOrdersAPI } from '@matix/api-client';
import { useParkedOrdersStore } from '../features/parked-orders';

/**
 * Boot the cashier API client. Called once before React mounts.
 * On 401, hard-redirects to /login (clears all stale state cleanly).
 */
const LOADER_EVENT = 'matix:loader';

export function dispatchLoaderEvent(activeRequests: number): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LOADER_EVENT, { detail: activeRequests }));
  }
}

export function bootClient(): void {
  const client = createApiClient({
    storageNamespace: 'pos:cashier',
    onUnauthorized: () => {
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    },
    onLoaderChange: dispatchLoaderEvent,
  });
  attachClient(client);

  // Wire the Park & Recall feature's HTTP adapter. The store is HTTP-
  // agnostic by design (testable without zustand-touching networking) and
  // takes its API on boot. We translate response shapes here to insulate
  // the store from wire-shape drift — when the API response evolves we
  // adjust this adapter, not the store.
  useParkedOrdersStore.getState().setApi({
    list: () => parkedOrdersAPI.list().then((r) => r.parked_orders),
    create: (input) =>
      parkedOrdersAPI.create(input).then((r) => ({
        id: r.parked_order_id,
        parked_at: r.parked_at,
        replayed: r.idempotent_replay,
      })),
    getById: (id) =>
      parkedOrdersAPI.getById(id).then((r) => ({
        id: r.id,
        customer_label: r.customer_label,
        item_count: r.item_count,
        total_value: r.total_value,
        note: r.note,
        parked_at: r.parked_at,
        snapshot: r.snapshot,
      })),
    deleteById: async (id) => {
      await parkedOrdersAPI.deleteById(id);
    },
  });
}

export { LOADER_EVENT };
